import React, { useState, useEffect, useReducer, useContext } from "react";
import { toast } from "react-toastify";
import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import IconButton from "@material-ui/core/IconButton";
import SearchIcon from "@material-ui/icons/Search";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import CircularProgress from "@material-ui/core/CircularProgress";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import { AccountCircle } from "@material-ui/icons";
import PageBase from "../../components/PageBase";
import whatsappIcon from "../../assets/nopicture.png";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import UserModal from "../../components/UserModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import toastError from "../../errors/toastError";
import {
  SocketContext,
  socketManager,
} from "../../context/Socket/SocketContext";
import UserStatusIcon from "../../components/UserModal/statusIcon";
import { getBackendUrl } from "../../config";
import { AuthContext } from "../../context/Auth/AuthContext";
import { Avatar } from "@material-ui/core";
import { TablePagination } from "@material-ui/core";
import ForbiddenPage from "../../components/ForbiddenPage";
import ChatBubbleOutlineIcon from "@material-ui/icons/ChatBubbleOutline";
import { useHistory } from "react-router-dom";
import usePlans from "../../hooks/usePlans";

const backendUrl = getBackendUrl();

const reducer = (state, action) => {
  if (action.type === "LOAD_USERS") {
    const users = action.payload;
    const newUsers = [];

    users.forEach((user) => {
      const userIndex = state.findIndex((u) => u.id === user.id);
      if (userIndex !== -1) {
        state[userIndex] = user;
      } else {
        newUsers.push(user);
      }
    });

    return [...state, ...newUsers];
  }

  if (action.type === "UPDATE_USERS") {
    const user = action.payload;
    const userIndex = state.findIndex((u) => u.id === user.id);

    if (userIndex !== -1) {
      state[userIndex] = user;
      return [...state];
    } else {
      return [user, ...state];
    }
  }

  if (action.type === "DELETE_USER") {
    const userId = action.payload;

    const userIndex = state.findIndex((u) => u.id === userId);
    if (userIndex !== -1) {
      state.splice(userIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(2),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  userAvatar: {
    width: theme.spacing(6),
    height: theme.spacing(6),
  },
  avatarDiv: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: theme.spacing(3),
  },
  loadingText: {
    marginLeft: theme.spacing(2),
  },
}));

const Users = () => {
  const classes = useStyles();
  const history = useHistory();

  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedUser, setSelectedUser] = useState(null);
  const [deletingUser, setDeletingUser] = useState(null);
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [users, dispatch] = useReducer(reducer, []);
  const { user: loggedInUser, socket } = useContext(AuthContext);
  const { profileImage } = loggedInUser;
  const companyId = loggedInUser.companyId;

  const [showInternalChat, setShowInternalChat] = useState(false);

  const { getPlanCompany } = usePlans();

  useEffect(() => {
    async function fetchData() {
      if (!companyId) {
        return;
      }

      try {
        setLoading(true);
        const planConfigs = await getPlanCompany(undefined, companyId);

        setShowInternalChat(planConfigs.plan.useInternalChat);
      } catch (err) {
        console.error("Error fetching plan:", err);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam, pageSize]);

  useEffect(() => {
    setLoading(true);
    const fetchUsers = async () => {
      try {
        const { data } = await api.get("/users/", {
          params: { searchParam, pageNumber, pageSize, limit: pageSize },
        });
        dispatch({ type: "RESET" });
        dispatch({ type: "LOAD_USERS", payload: data.users });
        setTotalCount(data.count || data.users?.length || 0);
      } catch (err) {
        toastError(err);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    };
    fetchUsers();
  }, [searchParam, pageNumber, pageSize]);

useEffect(() => {
  if (loggedInUser) {
    const companyId = loggedInUser.companyId;
    
    const onCompanyUser = (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_USERS", payload: data.user });
      }
      if (data.action === "delete") {
        dispatch({ type: "DELETE_USER", payload: +data.userId });
      }
    };
    
    socket.on(`company-${companyId}-user`, onCompanyUser);
    
    return () => {
      socket.off(`company-${companyId}-user`, onCompanyUser);
    };
  }
}, [socket, loggedInUser]);

  const handleOpenUserModal = () => {
    setSelectedUser(null);
    setUserModalOpen(true);
  };

  const handleCloseUserModal = () => {
    setSelectedUser(null);
    setUserModalOpen(false);
  };

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setUserModalOpen(true);
  };

  const handleDeleteUser = async (userId) => {
    try {
      await api.delete(`/users/${userId}`);
      toast.success(i18n.t("users.toasts.deleted"));
    } catch (err) {
      toastError(err);
    }
    setDeletingUser(null);
    setSearchParam("");
    setPageNumber(1);
  };

  const handleChangePage = (event, newPage) => {
    setPageNumber(newPage + 1);
  };

  const handleChangeRowsPerPage = (event) => {
    setPageSize(parseInt(event.target.value, 10));
    setPageNumber(1);
  };

const renderProfileImage = (user) => {
  const buildImageUrl = (userData) => {
    if (!userData.profileImage) {
      return whatsappIcon;
    }
    return `${backendUrl}/public/company${userData.companyId}/user/${userData.profileImage}`;
  };

  // Para o usuário logado, verificar localStorage também
  if (user.id === loggedInUser.id) {
    const savedProfileImage = localStorage.getItem("profileImage");
    const profileImageToUse = savedProfileImage || user.profileImage;
    
    const imageUrl = profileImageToUse 
      ? `${backendUrl}/public/company${user.companyId}/user/${profileImageToUse}`
      : whatsappIcon;

    return (
      <Avatar
        src={imageUrl}
        alt={user.name}
        className={classes.userAvatar}
        onError={(e) => {
          e.target.src = whatsappIcon;
        }}
      />
    );
  }
  
  // Para outros usuários
  return (
    <Avatar
      src={buildImageUrl(user)}
      alt={user.name}
      className={classes.userAvatar}
    />
  );
};

  const handleCreateChat = async (targetUser) => {
    try {
      setLoading(true);
      const { data } = await api.post("/chats", {
        users: [{ id: loggedInUser.id }, { id: targetUser.id }],
        isGroup: false,
        title: targetUser.name,
      });
      toast.success(i18n.t("users.toasts.chatCreated"));
      history.push(`/chats/${data.uuid}`);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleBackfillChats = async () => {
    if (
      !window.confirm(
        i18n.t("users.confirmations.backfillChats")
      )
    ) {
      return;
    }
    setLoading(true);
    try {
      await api.post("/chats/backfill");
      toast.success(i18n.t("users.toasts.chatsGenerated"));
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageBase
      className="users-page app-page-root"
      title={`${i18n.t("users.title")} (${users.length})`}
      actions={
        loggedInUser.profile !== "user" && (
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <TextField
              placeholder={i18n.t("contacts.searchPlaceholder")}
              type="search"
              value={searchParam}
              onChange={handleSearch}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon style={{ color: "gray" }} />
                  </InputAdornment>
                ),
              }}
            />
            <Button
              variant="contained"
              color="primary"
              onClick={handleOpenUserModal}
            >
              {i18n.t("users.buttons.add")}
            </Button>
            {loggedInUser.profile === "admin" && showInternalChat && (
              <Button
                variant="contained"
                color="secondary"
                onClick={handleBackfillChats}
                disabled={loading}
              >
                Gerar Chats Existentes
              </Button>
            )}
          </div>
        )
      }
      containerProps={{
        className: classes.mainPaper,
      }}
      footer={
        <TablePagination
          component="div"
          count={totalCount}
          page={Math.max(0, pageNumber - 1)}
          onPageChange={handleChangePage}
          rowsPerPage={pageSize}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 20, 50, 100]}
          labelRowsPerPage="Itens por página"
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} de ${count !== -1 ? count : `mais de ${to}`}`
          }
        />
      }
    >
      <ConfirmationModal
        title={
          deletingUser &&
          `${i18n.t("users.confirmationModal.deleteTitle")} ${
            deletingUser.name
          }?`
        }
        open={confirmModalOpen}
        onClose={() => setConfirmModalOpen(false)}
        onConfirm={() => handleDeleteUser(deletingUser.id)}
      >
        {i18n.t("users.confirmationModal.deleteMessage")}
      </ConfirmationModal>
      <UserModal
        open={userModalOpen}
        onClose={handleCloseUserModal}
        aria-labelledby="form-dialog-title"
        userId={selectedUser && selectedUser.id}
      />
      {loggedInUser.profile === "user" ? (
        <ForbiddenPage />
      ) : (
      <div className="page-table-wrapper app-table-zebra">
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell align="center">
                  {i18n.t("users.table.ID")}
                </TableCell>
                <TableCell align="center">
                  {i18n.t("users.table.status")}
                </TableCell>
                <TableCell align="center">{i18n.t("users.table.avatar")}</TableCell>
                <TableCell align="center">
                  {i18n.t("users.table.name")}
                </TableCell>
                <TableCell align="center">
                  {i18n.t("users.table.email")}
                </TableCell>
                <TableCell align="center">
                  {i18n.t("users.table.profile")}
                </TableCell>
                <TableCell align="center">
                  {i18n.t("users.table.startWork")}
                </TableCell>
                <TableCell align="center">
                  {i18n.t("users.table.endWork")}
                </TableCell>
                <TableCell align="center">
                  {i18n.t("users.table.actions")}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <>
                {users.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell align="center">{user.id}</TableCell>
                    <TableCell align="center">
                      <UserStatusIcon user={user} />
                    </TableCell>
                    <TableCell align="center">
                      <div className={classes.avatarDiv}>
                        {renderProfileImage(user)}
                      </div>
                    </TableCell>
                    <TableCell align="center">{user.name}</TableCell>
                    <TableCell align="center">{user.email}</TableCell>
                    <TableCell align="center">{user.profile}</TableCell>
                    <TableCell align="center">{user.startWork}</TableCell>
                    <TableCell align="center">{user.endWork}</TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        onClick={() => handleCreateChat(user)}
                        disabled={!showInternalChat}
                      >
                        <ChatBubbleOutlineIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleEditUser(user)}
                      >
                        <EditIcon />
                      </IconButton>

                      <IconButton
                        size="small"
                        onClick={(e) => {
                          setConfirmModalOpen(true);
                          setDeletingUser(user);
                        }}
                      >
                        <DeleteOutlineIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {loadingMore && (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                )}
              </>
            </TableBody>
          </Table>
          {loading && !loadingMore && (
            <div className={classes.loadingContainer}>
              <CircularProgress />
              <span className={classes.loadingText}>{i18n.t("loading")}</span>
            </div>
          )}
        </div>
      )}
    </PageBase>
  );
};

export default Users;
